<template>
	<!-- Backdrop overlay -->

	<div class="fixed inset-0 z-[99]">
		<div
			class=""
			:class="{
				'min-w-[296px] h-16 lg:h-10': !expandSearch,
				'min-w-[870px] shadow-xl': expandSearch,
				'h-80': expandSearch && searchQuery == '',
				'h-96': expandSearch && searchQuery.length > 0
			}"
			id="searchbar-mobile"
		>
			<div
				class="bg-white w-full fixed lg:absolute inset-0 lg:inset-auto lg:top-0 lg:left-0 lg:right-0 mx-auto flex flex-col lg:shadow-xl z-50 rounded-b-lg"
				@click.stop
			>
				<!-- Search Input Header -->
				<div class="relative flex items-center px-2 h-16 lg:h-10">
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 25"
						fill="rgba(129, 136, 152, 1)"
						xmlns="http://www.w3.org/2000/svg"
						class="text-slate-400 relative z-10"
					>
						<path
							d="M10.0799 1.93994C5.56867 1.93994 1.91992 5.58869 1.91992 10.0999C1.91992 14.6112 5.56867 18.2599 10.0799 18.2599C11.6905 18.2599 13.1812 17.7874 14.4449 16.9849L20.3399 22.8799L22.3799 20.8399L16.5599 15.0349C17.6062 13.6624 18.2399 11.9618 18.2399 10.0999C18.2399 5.58869 14.5912 1.93994 10.0799 1.93994ZM10.0799 3.85994C13.5355 3.85994 16.3199 6.64432 16.3199 10.0999C16.3199 13.5556 13.5355 16.3399 10.0799 16.3399C6.6243 16.3399 3.83992 13.5556 3.83992 10.0999C3.83992 6.64432 6.6243 3.85994 10.0799 3.85994Z"
							fill="currentColor"
						/>
					</svg>
					<input
						ref="searchInput"
						type="text"
						v-model="searchQuery"
						placeholder="Search for news, tickers or companies"
						@input="onSearchInput"
						@click="handleClick"
						class="pl-8 py-2 placeholder:text-sm text-base bg-transparent border-b border-slate-100 text-slate-900 placeholder-slate-400 h-16 lg:h-10 absolute top-0 left-0 right-0 lg:rounded-t-lg focus:outline-none"
					/>

					<div class="flex items-center gap-2 ml-auto">
						<button
							v-if="searchQuery.length > 0"
							@click="searchQuery = ''"
							class="text-slate-400 hover:text-slate-600 font-semibold text-sm transition-colors relative z-10 ml-auto px-2 py-1 rounded-md bg-slate-25 hover:bg-slate-100"
						>
							Clear
						</button>
						<button
							@click="$emit('close')"
							class="text-slate-400 hover:text-slate-600 font-semibold text-sm transition-colors relative z-10 ml-auto"
						>
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M18 6L6 18M6 6L18 18"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						</button>
					</div>
				</div>

				<!-- Trending Tickers - Shows when search is expanded but query is empty -->
				<div
					class="px-4 py-4 delay-150"
					v-if="searchQuery === ''"
				>
					<h3 class="text-base font-semibold text-slate-900 mb-3">
						Trending Tickers
					</h3>
					<div class="flex flex-wrap gap-2">
						<template v-if="trendingStocks.length == 0">
							<div
								v-for="i in 5"
								:key="i"
								class="h-12 w-48 animate-pulse rounded-full bg-slate-100"
							></div>
						</template>
						<template v-else>
							<NuxtLink
								v-for="stock in trendingStocks"
								:key="stock.symbol"
								:to="`/quote/${stock.symbol}/`"
								@click="$emit('close')"
								class="flex items-center gap-2 px-1 py-1 rounded-full bg-slate-25 hover:bg-slate-100"
							>
								<div class="flex items-center gap-2">
									<div
										v-if="imageErrors.has(stock.symbol)"
										class="w-6 h-6 rounded-full bg-black flex items-center justify-center"
									>
										<span class="text-white text-xs font-semibold">
											{{ stock.symbol.charAt(0) }}
										</span>
									</div>
									<img
										v-else
										:src="`https://stt-ts-resources.s3.us-east-1.amazonaws.com/stock-symbols/${stock.symbol}`"
										:alt="`Logo image for ${stock.symbol}`"
										class="w-6 h-6 rounded-full object-cover"
										@error="imageErrors.add(stock.symbol)"
									/>
									<span class="text-slate-900 font-semibold text-xs">
										{{ stock.symbol }}
									</span>
								</div>
								<div class="flex items-center mr-2">
									<span class="text-slate-400 font-medium text-xs">
										{{ stock.price?.toFixed(2) }}
									</span>
									<span
										class="text-xs font-medium"
										:class="{
											'text-red-400': stock.percentChange < 0,
											'text-success': stock.percentChange >= 0
										}"
									>
										{{ stock.percentChange >= 0 ? "+" : ""
										}}{{ stock.percentChange?.toFixed(2) }}%
									</span>
								</div>
							</NuxtLink>
						</template>
					</div>
				</div>

				<template v-if="searchQuery.length > 0">
					<!-- Results Container -->
					<div
						ref="resultsContainer"
						class="flex-1 lg:max-h-[420px] lg:min-h-[320px] overflow-y-auto pt-3 pb-16 relative"
					>
						<!-- Symbols Section -->
						<div
							v-if="results.stocks && results.stocks.length > 0"
							class="mb-4"
						>
							<div class="px-4 mb-2">
								<h3 class="text-base font-semibold text-slate-900">Symbols</h3>
							</div>
							<NuxtLink
								v-for="(stock, index) in results.stocks.slice(0, 3)"
								:key="stock.symbol"
								:to="`/quote/${stock.symbol}/`"
								@click="$emit('close')"
								:data-result-index="index"
								:ref="(el) => setResultRef(el, index)"
								class="flex items-center justify-between px- hover:bg-slate-25 w-full focus:outline-none"
								:class="{
									'bg-slate-25': focusedIndex === index
								}"
							>
								<div
									class="py-2 flex items-center justify-between w-full px-4"
									:class="{
										'border-b border-slate-50':
											index !== results.stocks.slice(0, 3).length - 1
									}"
								>
									<div
										v-if="imageErrors.has(stock.symbol)"
										class="w-8 h-8 rounded-md bg-black flex items-center justify-center mr-4"
									>
										<span class="text-white text-sm font-semibold">
											{{ stock.symbol.charAt(0) }}
										</span>
									</div>
									<img
										v-else
										:src="`https://stt-ts-resources.s3.us-east-1.amazonaws.com/stock-symbols/${stock.symbol}`"
										:alt="`Logo image for ${stock.symbol}`"
										class="w-8 h-8 rounded-md object-cover mr-4 bg-slate-100/[.2]"
										@error="imageErrors.add(stock.symbol)"
									/>
									<div class="flex items-center gap-3 w-full">
										<h4 class="text-base font-medium text-slate-900">
											{{ stock.symbol }}
										</h4>
										<span class="text-sm text-slate-500">{{ stock.longName }}</span>
									</div>
									<span class="text-sm text-slate-400">{{ stock.exchange }}</span>
								</div>
							</NuxtLink>
						</div>

						<!-- Progress Bar - Shows when loading API results -->
						<div
							v-if="isLoading"
							class="px-4 mb-4"
						>
							<div class="w-full bg-slate-100 rounded-full h-1 overflow-hidden">
								<div
									class="h-full bg-blue-300 rounded-full progress-bar"
									:style="{ width: progressWidth + '%' }"
								></div>
							</div>
						</div>

						<!-- News Section -->
						<div
							v-if="results.news && results.news.length > 0"
							class="mb-4"
						>
							<div class="px-4 mb-2">
								<h3 class="text-base font-semibold text-slate-900">News</h3>
							</div>
							<div>
								<NuxtLink
									v-for="(newsItem, index) in results.news"
									:key="newsItem.id"
									:to="`/news/${newsItem.slug}/`"
									@click="$emit('close')"
									:data-result-index="results.stocks.slice(0, 3).length + index"
									:ref="
										(el) => setResultRef(el, results.stocks.slice(0, 3).length + index)
									"
									class="block px-4 hover:bg-slate-25 focus:outline-none"
									:class="{
										'bg-slate-25':
											focusedIndex === results.stocks.slice(0, 3).length + index
									}"
								>
									<div
										class="py-3 border-b border-slate-50"
										:class="{
											'border-b border-slate-50': index !== results.news.length - 1
										}"
									>
										<h4
											class="text-base font-medium leading-snug text-slate-900"
											v-html="newsItem.title.rendered"
										></h4>
										<span class="text-sm text-slate-400">
											{{ formatDate(newsItem.date) }}
										</span>
									</div>
								</NuxtLink>
							</div>
						</div>

						<!-- Related Research Section (Posts & Pages combined) -->
						<div
							v-if="relatedResearch.length > 0"
							class="mb-4"
						>
							<div class="px-4 mb-2">
								<h3 class="text-base font-semibold text-slate-900">Related Research</h3>
							</div>
							<div>
								<NuxtLink
									v-for="(item, index) in relatedResearch"
									:key="item.id"
									:to="item.url"
									@click="$emit('close')"
									:data-result-index="
										results.stocks.slice(0, 3).length + results.news.length + index
									"
									:ref="
										(el) =>
											setResultRef(
												el,
												results.stocks.slice(0, 3).length + results.news.length + index
											)
									"
									class="block px-4 hover:bg-slate-25 focus:outline-none"
									:class="{
										'bg-slate-25':
											focusedIndex ===
											results.stocks.slice(0, 3).length + results.news.length + index
									}"
								>
									<div
										class="py-3 border-b border-slate-50"
										:class="{
											'border-b border-slate-50': index !== relatedResearch.length - 1
										}"
									>
										<h4 class="text-base font-medium leading-snug text-slate-900 mb-1">
											{{ item.title.rendered }}
										</h4>
										<span class="text-sm text-slate-400">
											{{ formatDate(item.date) }}
										</span>
									</div>
								</NuxtLink>
							</div>
						</div>

						<!-- No Results -->
						<div
							v-if="hasSearched && !hasResults && !isLoading"
							class="flex-1 flex flex-col"
						>
							<div
								class="flex-1 px-4 py-12 text-center flex flex-col items-center justify-center gap-4"
							>
								<div class="relative">
									<svg
										width="64"
										height="64"
										viewBox="0 0 24 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										class="text-slate-300"
									>
										<circle
											cx="11"
											cy="11"
											r="7"
											stroke="currentColor"
											stroke-width="2"
											stroke-linecap="round"
										/>
										<path
											d="M20 20L17 17"
											stroke="currentColor"
											stroke-width="2"
											stroke-linecap="round"
										/>
									</svg>
									<div class="absolute -bottom-1 -right-1 bg-white rounded-full p-1">
										<svg
											viewBox="0 0 48 48"
											class=""
											height="16"
											width="16"
											style="stroke-width: 0"
										>
											<path
												d="M24.993 46.424c-12.13 0-22-9.87-22-22s9.87-22 22-22 22 9.87 22 22-9.87 22-22 22zm0-40c-9.925 0-18 8.075-18 18 0 9.926 8.075 18 18 18s18-8.074 18-18c0-9.924-8.075-18-18-18zM24.993 27.424c-1.104 0-2-.895-2-2v-10c0-1.104.896-2 2-2s2 .896 2 2v10c0 1.105-.895 2-2 2zM22.993 33.424a2 2 0 1 0 4 0 2 2 0 1 0-4 0z"
											></path>
										</svg>
									</div>
								</div>
								<div class="flex flex-col gap-1">
									<p class="text-slate-900 font-semibold text-lg">
										No matching results for '{{ searchQuery }}'
									</p>
								</div>
							</div>
							<div class="mt-auto px-4 py-3 bg-slate-100">
								<p class="text-slate-700 text-sm text-center">
									<span class="font-semibold text-slate-900">Tip:</span>
									Try a valid symbol or a specific company name for relevant results
								</p>
							</div>
						</div>

						<!-- Spacer for legend -->
						<div class="h-12" />
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, defineEmits, nextTick } from "vue";
import Fuse from "fuse.js";
import { useStocksStore } from "~/stores/useStocksStore";
import { normalizeExchange } from "~/utils/exchange-normalizer";

interface Stock {
	symbol: string;
	longName: string;
	exchange: string;
	SYMBOL?: string;
	NAME?: string;
	[key: string]: any;
}

interface NewsItem {
	id: number;
	title: { rendered: string };
	slug: string;
	date: string;
}

interface Post {
	id: number;
	title: { rendered: string };
	slug: string;
	date: string;
}

interface Page {
	id: number;
	title: { rendered: string };
	slug: string;
	date: string;
}

interface SearchResults {
	stocks: Stock[];
	news: NewsItem[];
	posts: Post[];
	pages: Page[];
}

const props = defineProps({
	isVisible: {
		type: Boolean,
		default: false
	}
});

const emit = defineEmits({
	close: null,
	open: null
});

const searchQuery = ref("");
const searchInput = ref<HTMLInputElement | null>(null);
const resultsContainer = ref<HTMLDivElement | null>(null);
const results = ref<SearchResults>({
	stocks: [],
	news: [],
	posts: [],
	pages: []
});
const isLoading = ref(false);
const hasSearched = ref(false);
const fuse = ref<Fuse<Stock> | null>(null);
const progressWidth = ref(0);

const expandSearch = ref(false);
const trendingStocks = ref<
	Array<{
		symbol: string;
		name: string;
		price: number;
		change: number;
		percentChange: number;
	}>
>([]);

const focusedIndex = ref<number>(-1);
const resultRefs = ref<Map<number, any>>(new Map());
const imageErrors = ref<Set<string>>(new Set());

const stocksStore = useStocksStore();

let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const route = useRoute();
// Computed properties
const hasResults = computed(() => {
	return (
		results.value.stocks.length > 0 ||
		results.value.news.length > 0 ||
		results.value.posts.length > 0 ||
		results.value.pages.length > 0
	);
});

// Combine posts and pages for Related Research section
const relatedResearch = computed(() => {
	const combined = [
		...results.value.posts.map((post) => ({
			...post,
			url: `/${post.slug}/`
		})),
		...results.value.pages.map((page) => ({
			...page,
			url: `/${page.slug}/`
		}))
	];
	// Sort by date (most recent first)
	return combined.sort(
		(a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
	);
});

// Calculate total results count for keyboard navigation
const totalResults = computed(() => {
	const stockCount = Math.min(results.value.stocks.length, 3);
	const newsCount = results.value.news.length;
	const researchCount = relatedResearch.value.length;
	return stockCount + newsCount + researchCount;
});

// Focus input on mount and when component becomes visible
onMounted(() => {
	// Initialize the store if needed
	if (stocksStore.symbols_list.length === 0) {
		stocksStore.init();
	}
});

// Watch for symbols list and initialize Fuse
watch(
	() => stocksStore.symbols_list,
	(newData) => {
		if (import.meta.client && newData && newData.length > 0) {
			// Transform the data to match the expected format
			const searchData = newData.map((item: any) => ({
				symbol: item.SYMBOL || item.symbol,
				longName: item.NAME || item.name || item.longName,
				exchange: item.exchange || "",
				...item
			}));

		fuse.value = new Fuse(searchData, {
			keys: ["symbol", "longName", "NAME", "SYMBOL"],
			threshold: 0.3,
			includeScore: false,
			findAllMatches: true,
			isCaseSensitive: false
		});

		// If the user already typed a query before Fuse was ready, backfill stock results
		if (searchQuery.value.trim().length > 0) {
			results.value.stocks = searchStocks(searchQuery.value.trim());
		}
	}
},
{
	deep: true,
	immediate: true
}
);

watch(
	() => stocksStore.most_active_stocks,
	(newData) => {
		if (import.meta.client && newData) {
			const shuffled = Object.values(newData).sort(() => Math.random() - 0.5);
			const arrays = shuffled.slice(0, 10);

			if (arrays.length > 0) {
				trendingStocks.value = arrays.map((stock) => {
					return {
						symbol: stock.SYMBOL,
						name: stock.NAME,
						price: stock.LAST,
						change: stock.NET_CHG,
						percentChange: stock.PC_CHG
					};
				});
			}
		}
	},
	{
		deep: true,
		immediate: true
	}
);

// Format date helper
const formatDate = (dateString: string): string => {
	const date = new Date(dateString);
	const now = new Date();
	const diffInMs = now.getTime() - date.getTime();
	const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
	const diffInDays = Math.floor(diffInHours / 24);

	if (diffInHours < 1) {
		const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
		return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
	} else if (diffInHours < 24) {
		return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
	} else if (diffInDays < 7) {
		return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
	} else {
		return date.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric"
		});
	}
};

// Set result ref for keyboard navigation
const setResultRef = (el: any, index: number) => {
	if (el) {
		resultRefs.value.set(index, el);
	} else {
		resultRefs.value.delete(index);
	}
};

// Navigate to focused result
const navigateToFocusedResult = () => {
	const focusedElement = resultRefs.value.get(focusedIndex.value);
	if (focusedElement && focusedElement.$el) {
		// For NuxtLink component, we need to access the actual link
		const linkElement = focusedElement.$el;
		if (linkElement && linkElement.click) {
			linkElement.click();
		}
	}
};

// Clear search and refocus input
const clearSearch = () => {
	searchQuery.value = "";
	results.value = { stocks: [], news: [], posts: [], pages: [] };
	hasSearched.value = false;
	focusedIndex.value = -1;
	resultRefs.value.clear();
	searchInput.value?.focus();
	expandSearch.value = false;
};

// Search stocks locally with Fuse
const searchStocks = (query: string): Stock[] => {
	if (!fuse.value || !query) {
		return [];
	}

	try {
		const searchResults = fuse.value.search(query);
		return searchResults
			.map(({ item }) => {
				// Normalize the exchange using the utility function
				const stock = { ...item };
				stock.exchange = normalizeExchange(stock.exchange || "");
				stock.symbol = stock.SYMBOL || stock.symbol;
				stock.longName = stock.NAME || stock.longName;
				return stock;
			})
			.slice(0, 5);
	} catch (error) {
		// Silently handle errors and return empty results
		return [];
	}
};

// Search function
const performSearch = async () => {
	if (!searchQuery.value || searchQuery.value.trim().length < 1) {
		results.value = { stocks: [], news: [], posts: [], pages: [] };
		hasSearched.value = false;
		focusedIndex.value = -1;
		return;
	}

	hasSearched.value = true;
	const query = searchQuery.value.trim();

	// Search stocks immediately with Fuse (fast, client-side)
	const stockResults = searchStocks(query);
	results.value.stocks = stockResults;

	// Focus the first result immediately if we have stock results
	if (stockResults.length > 0) {
		await nextTick();
		focusedIndex.value = 0;
	}

	// If no stock results, perform article search only
	if (stockResults.length === 0) {
		// Reset and start progress bar
		progressWidth.value = 0;
		await nextTick();

		// Start animating progress bar gradually
		const progressInterval = setInterval(() => {
			if (progressWidth.value < 90) {
				progressWidth.value = Math.min(progressWidth.value + 10, 90);
			}
		}, 200);

		try {
			const response = await $fetch<Omit<SearchResults, "stocks">>("/api/search", {
				params: {
					q: searchQuery.value,
					article: "true"
				}
			});

			// Stop the interval and complete progress bar
			clearInterval(progressInterval);
			progressWidth.value = 100;

			// Update results with API data
			results.value = {
				...results.value,
				news: response.news || [],
				posts: response.posts || [],
				pages: response.pages || []
			};

			// Focus the first result after search completes
			if (totalResults.value > 0) {
				await nextTick();
				focusedIndex.value = 0;
			}
		} catch (error) {
			// Stop the interval and complete progress bar even on error
			clearInterval(progressInterval);
			progressWidth.value = 100;

			// Keep stocks results even if API fails
			results.value = {
				...results.value,
				news: [],
				posts: [],
				pages: []
			};

			// Still focus first result if we have results
			if (totalResults.value > 0) {
				await nextTick();
				focusedIndex.value = 0;
			}
		} finally {
			// Small delay to show completion, then hide
			setTimeout(() => {
				isLoading.value = false;
				progressWidth.value = 0;
			}, 200);
		}
	} else {
		// We have stock results, search with formatted query
		const firstStock = stockResults[0] as Stock;
		const formattedQuery = `${firstStock.symbol} - ${firstStock.longName}`;

		// Reset and start progress bar
		progressWidth.value = 0;
		await nextTick();

		// Start animating progress bar gradually
		const progressInterval = setInterval(() => {
			if (progressWidth.value < 90) {
				progressWidth.value = Math.min(progressWidth.value + 10, 90);
			}
		}, 200);

		try {
			const response = await $fetch<Omit<SearchResults, "stocks">>("/api/search", {
				params: {
					q: formattedQuery
				}
			});

			// Stop the interval and complete progress bar
			clearInterval(progressInterval);
			progressWidth.value = 100;

			// Update results with API data
			results.value = {
				...results.value,
				news: response.news || [],
				posts: response.posts || [],
				pages: response.pages || []
			};

			// Focus the first result after search completes
			if (totalResults.value > 0) {
				await nextTick();
				focusedIndex.value = 0;
			}
		} catch (error) {
			// Stop the interval and complete progress bar even on error
			clearInterval(progressInterval);
			progressWidth.value = 100;

			// Keep stocks results even if API fails
			results.value = {
				...results.value,
				news: [],
				posts: [],
				pages: []
			};

			// Still focus first result if we have stock results
			if (totalResults.value > 0) {
				await nextTick();
				focusedIndex.value = 0;
			}
		} finally {
			// Small delay to show completion, then hide
			setTimeout(() => {
				isLoading.value = false;
				progressWidth.value = 0;
			}, 200);
		}
	}
};

// Debounced search input handler
const onSearchInput = () => {
	if (searchTimeout) {
		clearTimeout(searchTimeout);
	}

	// Clear old API results immediately when user starts typing new query
	// Keep stocks results since they're instant from Fuse
	results.value.news = [];
	results.value.posts = [];
	results.value.pages = [];
	focusedIndex.value = -1;
	progressWidth.value = 0;

	// Show loading immediately if query is long enough
	if (searchQuery.value.trim().length >= 1) {
		isLoading.value = true;
	}

	searchTimeout = setTimeout(() => {
		performSearch();
	}, 1000);
};

const handleInputKeyDown = (event: KeyboardEvent) => {
	// Handle Escape key
	if (event.key === "Escape") {
		// Clear any pending search timeout
		if (searchTimeout) {
			clearTimeout(searchTimeout);
			searchTimeout = null;
		}

		// Close search but keep query and results
		expandSearch.value = false;
		isLoading.value = false;
		focusedIndex.value = -1;
		resultRefs.value.clear();
		searchInput.value?.blur();
		emit("close");
		return;
	}
	// Handle Ctrl+Q to clear search
	if ((event.ctrlKey || event.metaKey) && event.key === "q") {
		event.preventDefault();
		clearSearch();
		return;
	}

	// Handle arrow down to start navigating results
	if (event.key === "ArrowDown") {
		event.preventDefault();
		event.stopPropagation(); // Stop propagation to prevent global handler from also firing
		if (totalResults.value >= -1) {
			if (focusedIndex.value < totalResults.value - 1) {
				focusedIndex.value += 1;
			}
		}
		return;
	}

	// Handle arrow up when in input (does nothing)
	if (event.key === "ArrowUp") {
		if (focusedIndex.value >= 0) {
			focusedIndex.value -= 1;
		}

		event.preventDefault();
		event.stopPropagation(); // Stop propagation to prevent global handler from also firing
		return;
	}

	// Handle Enter when focused on input (not on a result)
	if (event.key === "Enter" && focusedIndex.value >= 0) {
		event.preventDefault();
		navigateToFocusedResult();
	}
};

const handleClick = () => {
	expandSearch.value = true;
	emit("open");
};

// Watch focusedIndex to track changes
watch(
	focusedIndex,
	() => {
		// Track focused index changes
	},
	{ immediate: false }
);

// Watch isVisible prop to sync with expandSearch
watch(
	() => props.isVisible,
	(newValue) => {
		if (newValue) {
			// When component becomes visible, expand the search
			expandSearch.value = true;
			// Focus the input after a short delay to ensure DOM is ready
			nextTick(() => {
				searchInput.value?.focus();
			});
		}
		// When component becomes hidden, we keep expandSearch state
		// so that query and results are preserved
	}
);

watch(expandSearch, (newValue) => {
	if (import.meta.client) {
		if (newValue) {
			const container = document.querySelector("#searchbar-mobile");

			if (container) {
				container.addEventListener("keydown", (event: Event) => {
					const keyboardEvent = event as KeyboardEvent;
					handleInputKeyDown(keyboardEvent);
				});
			}
		}
	}
});

watch(
	() => route.fullPath,
	(newValue, oldValue) => {
		if (newValue !== oldValue) {
			clearSearch();
			searchInput.value?.blur();
		}
	},
	{ immediate: true }
);
</script>

<style scoped>
/* Ensure proper z-index layering */
#searchbar-mobile {
	z-index: 100;
}

/* Make scrollbar transparent on results container */
.overflow-y-auto {
	scrollbar-color: transparent transparent;
	scrollbar-width: thin;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background: transparent;
		border-radius: 4px;
	}
}

.progress-bar {
	transition: width 0.3s ease-out;
}
</style>
